import { useState, useEffect, useCallback, useRef } from 'react';

interface UseAutoHideControlsOptions {
  timeout?: number;
  enabled?: boolean;
  alwaysShowWhenPaused?: boolean;
  isPaused?: boolean;
}

interface UseAutoHideControlsResult {
  isVisible: boolean;
  showControls: () => void;
}

export function useAutoHideControls({
  timeout = 3000,
  enabled = true,
  alwaysShowWhenPaused = true,
  isPaused = false,
}: UseAutoHideControlsOptions = {}): UseAutoHideControlsResult {
  const [isVisible, setIsVisible] = useState(true);
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const scheduleHide = useCallback(() => {
    clearHideTimeout();

    // Don't hide if paused and alwaysShowWhenPaused is true
    if (alwaysShowWhenPaused && isPaused) {
      return;
    }

    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
    }, timeout);
  }, [timeout, clearHideTimeout, alwaysShowWhenPaused, isPaused]);

  const showControls = useCallback(() => {
    setIsVisible(true);
    if (enabled) {
      scheduleHide();
    }
  }, [enabled, scheduleHide]);

  // Handle enabled state changes
  useEffect(() => {
    if (enabled) {
      showControls();
    } else {
      clearHideTimeout();
      setIsVisible(true);
    }

    return clearHideTimeout;
  }, [enabled, showControls, clearHideTimeout]);

  // Keep controls visible when paused
  useEffect(() => {
    if (alwaysShowWhenPaused && isPaused && enabled) {
      clearHideTimeout();
      setIsVisible(true);
    } else if (enabled && !isPaused) {
      scheduleHide();
    }
  }, [isPaused, alwaysShowWhenPaused, enabled, clearHideTimeout, scheduleHide]);

  return {
    isVisible,
    showControls,
  };
}
